import {gql} from 'apollo-angular';
import {
    ADDRESS_FRAGMENT,
    ASSET_FRAGMENT,
    COUNTRY_FRAGMENT,
    CART_FRAGMENT,
    ERROR_RESULT_FRAGMENT,
    LOGO_SECTION_FRAGMENT,
    STRIPE_CAPABILITY_FRAGMENT,
    PAGE_COMPONENT_FRAGMENT,
    WEB_PRESENCE_FRAGMENT,
    PRODUCT_CUSTOMIZATION_OPTION_FRAGMENT,
    CHANNEL_RETURN_ADDRESS_FRAGMENT,
    ORDER_ADDRESS_FRAGMENT,
} from './fragments.graphql';

export const GET_CUSTOMER_ADDRESSES = gql`
    query GetCustomerAddresses {
        activeCustomer {
            id
            addresses {
                ...Address
            }
        }
    }
    ${ADDRESS_FRAGMENT}
`;
export const DELETE_CUSTOMER_ADDRESS = gql`
mutation DeleteCustomerAddress($id: ID!) {
    deleteCustomerAddress(id: $id) {
        success
    }
}
`;
export const GET_AVAILABLE_COUNTRIES = gql`
    query GetAvailableCountries {
        availableCountries {
            ...Country
        }
    }
    ${COUNTRY_FRAGMENT}
`;

export const GET_ACTIVE_CUSTOMER = gql`
    query GetActiveCustomer {
        activeCustomer {
            id
            firstName
            lastName
            emailAddress
            phoneNumber
            customFields {
                nickname
            }
        }
    }
`;

export const GET_ELIGIBLE_PAYMENT_CAPABILITIES = gql`
    query GetEligiblePaymentCapabilities {
        eligiblePaymentCapabilities {
            ...StripeCapability
        }
    }
    ${STRIPE_CAPABILITY_FRAGMENT}
`;

export const GUEST_SUBMIT_PRODUCT_REVIEW = gql`
    mutation GuestSubmitProductReview($input: SubmitProductReviewInput!) {
        guestSubmitProductReview(input: $input) {
            __typename
            ...on ProductReview {
                id
                summary
                body
                rating
                authorName
                state
            }
            ...on SubmitProductReviewError {
                submitProductReviewErrorMessage
            }
        }
    }
`;

export const SUBMIT_PRODUCT_REVIEW = gql`
    mutation SubmitProductReview($input: SubmitProductReviewInput!) {
        submitProductReview(input: $input) {
            __typename
            ...on ProductReview {
                id
                summary
                body
                rating
                authorName
                state
            }
            ...on SubmitProductReviewError {
                submitProductReviewErrorMessage
            }
        }
    }
`;

export const GET_REVIEWS_FOR_PRODUCT = gql`
    query GetReviewForProduct($productId: ID!, $options: ProductReviewListOptions) {
        product(id: $productId) {
            id
            reviews(options: $options) {
                items {
                    id
                    summary
                    body
                    rating
                    authorName
                    state
                    featuredAsset {
                        ...Asset
                    }
                }
                totalItems
            }
        }
    }
    ${ASSET_FRAGMENT}
`;

export const GET_PRODUCT_REVIEW = gql`
    query GetProductReview($productId: ID!, $customerId: ID) {
        productReview(productId: $productId, customerId: $customerId) {
            id
            summary
            body
            rating
            authorName
            state
            featuredAsset {
                ...Asset
            }
        }
    }
    ${ASSET_FRAGMENT}
`;

export const GUEST_GET_PRODUCT_REVIEW = gql`
    query GuestGetProductReview($productId: ID!, $customerId: ID) {
        guestProductReview(productId: $productId, customerId: $customerId) {
            id
            summary
            body
            rating
            authorName
            state
            featuredAsset {
                ...Asset
            }
        }
    }
    ${ASSET_FRAGMENT}
`;

export const GET_FACET = gql`
    query GetFacet($id: ID!) {
        facet(id: $id) {
            id
            name
            values {
                id
                name
                code
            }
        }
    }
`;


export const SEARCH_PRODUCTS = gql`
    query SearchProducts($input: SearchInput!) {
        search(input: $input) {
            items {
                productId
                slug
                productName
                description
                productVariantId
                productSubtitle
                productVariantMSRP
                inStock
                price {
                    ... on PriceRange {
                        min
                        max
                    }
                    ... on SinglePrice {
                        value
                    }
                }
                productAsset {
                    id
                    preview
                    focalPoint {
                        x
                        y
                    }
                    videoUrl
                    imageUrl
                }
                productAlternativeAsset {
                    id
                    preview
                    focalPoint {
                        x
                        y
                    }
                    videoUrl
                    imageUrl
                }
                facetIds
                facetValueIds
                isProductCustomizable
                isProductVariantCustomizable
                averageRating
                reviewCount
                featuredReview {
                    id
                    createdAt
                    rating
                    authorName
                    summary
                    body
                }
                productCardBackgroundColor
            }
            totalItems
            facetValues {
                count
                facetValue {
                    id
                    name
                    facet {
                        id
                        name
                        code
                    }
                }
            }
        }
    }
`;

export const SEARCH_VARIANTS = gql`
    query SearchVariants($input: SearchInput!) {
        search(input: $input) {
            items {
                productId
                slug
                productName
                productVariantName
                description
                productVariantId
                productSubtitle
                productVariantMSRP
                inStock
                price {
                    ... on PriceRange {
                        min
                        max
                    }
                    ... on SinglePrice {
                        value
                    }
                }
                productAsset {
                    id
                    preview
                    focalPoint {
                        x
                        y
                    }
                    videoUrl
                    imageUrl
                }
                productAlternativeAsset {
                    id
                    preview
                    focalPoint {
                        x
                        y
                    }
                    videoUrl
                    imageUrl
                }
                productVariantAsset {
                    id
                    preview
                    focalPoint {
                        x
                        y
                    }
                    videoUrl
                    imageUrl
                }
                productVariantAlternativeAsset {
                    id
                    preview
                    focalPoint {
                        x
                        y
                    }
                    videoUrl
                    imageUrl
                }
                facetIds
                facetValueIds
                isProductCustomizable
                isProductVariantCustomizable
                averageRating
                reviewCount
                featuredReview {
                    id
                    createdAt
                    rating
                    authorName
                    summary
                    body
                }
                productCardBackgroundColor
            }
            totalItems
            facetValues {
                count
                facetValue {
                    id
                    name
                    facet {
                        id
                        name
                        code
                    }
                }
            }
        }
    }
`;

export const GET_COLLECTION = gql`
    query GetCollection($id: ID, $slug: String) {
        collection(id: $id, slug: $slug) {
            id
            name
            slug
            description
            featuredAsset {
                ...Asset
            }
            breadcrumbs {
                id
                slug
                name
            }
            children {
                id
                slug
                featuredAsset {
                    ...Asset
                }
                name
            }
            customFields {
                itemOrderData
            }
        }
    }
    ${ASSET_FRAGMENT}
`;

export const GET_COLLECTIONS = gql`
    query GetCollections($options: CollectionListOptions) {
        collections(options: $options) {
            items {
                id
                name
                slug
                parent {
                    id
                    name
                    slug
                }
                featuredAsset {
                    ...Asset
                }
            }
        }
    }
    ${ASSET_FRAGMENT}
`;

export const GET_STORE_REVISION_INFO = gql`
    query GetStoreRevisionInfo($identifier: String!) {
        storeInfo(identifier: $identifier) {
            id
            name
            description
            storeUrl
            identifier
            isPublished
            status
            sellerChannel {
                id
                code
                token
                currencyCode
                defaultLanguageCode
                customFields {
                    notificationEmail
                    returnAddress {
                        ...ReturnAddress
                    }
                }
            }
            siteRoutes {
                slug
                name
                type
            }
            siteStyles {
                siteTitle
                displayHeaderMenu
                displayFooterMenu
                displayStoreName
                displayHeaderMessage
                copyrightNotice
                disclaimer
                pdpAssetsArrowType
                mobileBuyBannerType
            }
            headerMessageConfig {
                displayOrder
                switchType
                autoSwitchInterval
                messageList
            }
            colorConfig {
                mainColor
                mainTextColor
                secondaryColor
                secondaryTextColor
                headerBackgroundColor
                headerFontColor
                footerBackgroundColor
                footerFontColor
            }
            logoSectionData {
                ...LogoSection
            }
            snsInfoSection {
                instagram
                facebook
                xTwitter
                youtube
                tiktok
            }
            navigationMenu {
                targetType
                label
                pageSlug
                pageSection
                builtInPage
                customSlug
                websiteUrl
                targetProductId
                targetProductSlug
                targetCollectionId
                targetCollectionSlug
            }
            mobileSecondaryNavigationMenu {
                targetType
                label
                pageSlug
                pageSection
                builtInPage
                customSlug
                websiteUrl
                targetProductId
                targetProductSlug
                targetCollectionId
                targetCollectionSlug
            }
            footerNavigationColumns {
                name
                items {
                    targetType
                    label
                    pageSlug
                    pageSection
                    builtInPage
                    customSlug
                    websiteUrl
                    targetProductId
                    targetProductSlug
                    targetCollectionId
                    targetCollectionSlug
                }
            }
            policyContentList {
                type
                title
                path
                content
            }
            supportEmail
            returnPolicy {
                enableCancel
                cancellableTimeInHours
                cancelExpiredNotification
                enableReturn
                returnableTimeInDays
                returnExpiredNotification
                disabledPolicyNotification
            }
            externalServicesSettings {
                omnisend {
                    enabled
                    brandId
                }
                gtag {
                    enabled
                    trackingId
                    mode
                }
                clarity {
                    enabled
                    trackingId
                }
                tawkio {
                    enabled
                    chatId
                    embedChatId
                }
            }
            contactUsAddress {
                ...OrderAddress
            }
        }
    }
    ${CHANNEL_RETURN_ADDRESS_FRAGMENT}
    ${LOGO_SECTION_FRAGMENT}
    ${ORDER_ADDRESS_FRAGMENT}
`;

export const GET_STORE_PAGE = gql`
    query GetStorePage($identifier: String!, $slug: String!) {
        storePage(identifier: $identifier, slug: $slug) {
            slug
            name
            type
            webPresence {
                ...WebPresence
            }
            navigationMenu {
                targetType
                label
                pageSlug
                pageSection
                builtInPage
                customSlug
                websiteUrl
                targetProductId
                targetProductSlug
                targetCollectionId
                targetCollectionSlug
            }
            pageComponentList {
                ...PageComponent
            }
        }
    }
    ${WEB_PRESENCE_FRAGMENT}
    ${PAGE_COMPONENT_FRAGMENT}
`;

export const GET_PRODUCT_PAGE = gql`
    query GetProductPage($identifier: String!, $slug: String!) {
        productPage(identifier: $identifier, slug: $slug) {
            slug
            name
            type
            webPresence {
                ...WebPresence
            }
            navigationMenu {
                targetType
                label
                pageSlug
                pageSection
                builtInPage
                customSlug
                websiteUrl
                targetProductId
                targetProductSlug
                targetCollectionId
                targetCollectionSlug
            }
            pageComponentList {
                ...PageComponent
            }
            productInfo {
                id
                name
                slug
                createdAt
                description
                variants {
                    id
                    name
                    options {
                        id
                        code
                        name
                        groupId
                        group {
                            code
                        }
                    }
                    currencyCode
                    price
                    priceWithTax
                    sku
                    stockLevel
                    featuredAsset {
                        ...Asset
                    }
                    assets {
                        ...Asset
                    }
                    customFields {
                        MSRP
                        customizationTemplateDisplaySize
                        gtin
                        mpn
                        variantSubtitle
                    }
                    customizationOptions {
                        ...ProductCustomizationOption
                    }
                }
                optionGroups {
                    id
                    code
                    name
                    customFields {
                        displayType
                        priority
                    }
                    options {
                        id
                        code
                        name
                        customFields {
                            Priority
                        }
                    }
                }
                facetValues {
                    ...FacetValue
                }
                customFields {
                    OGDescription
                    OGImage {
                        id
                        preview
                        imageUrl
                    }
                    ProductSubtitle
                    ProductFeatures
                    ProductDisclaimer
                    reviewRating
                    reviewCount
                    salesNumber
                    salesStatement
                    featuredReview {
                        id
                        createdAt
                        rating
                        authorName
                        summary
                        body
                        featuredAsset {
                            ...Asset
                        }
                    }
                    customizationTemplateDisplaySize
                    productKeySellingPointList
                    productCardBackgroundColor
                    defaultVariant {
                        id
                    }
                    displayFeaturedReview
                }
                featuredAsset {
                    ...Asset
                }
                assets {
                    ...Asset
                }
                customizationOptions {
                    ...ProductCustomizationOption
                }
            }
        }
    }
    ${WEB_PRESENCE_FRAGMENT}
    ${PAGE_COMPONENT_FRAGMENT}
    ${ASSET_FRAGMENT}
    ${PRODUCT_CUSTOMIZATION_OPTION_FRAGMENT}
`;

export const GET_COLLECTION_PAGE = gql`
    query GetCollectionPage($identifier: String!, $slug: String!) {
        collectionPage(identifier: $identifier, slug: $slug) {
            slug
            name
            type
            webPresence {
                ...WebPresence
            }
            navigationMenu {
                targetType
                label
                pageSlug
                pageSection
                builtInPage
                customSlug
                websiteUrl
                targetProductId
                targetProductSlug
                targetCollectionId
                targetCollectionSlug
            }
            pageComponentList {
                ...PageComponent
            }
            collectionInfo {
                id
                name
                slug
                description
                featuredAsset {
                    ...Asset
                }
                breadcrumbs {
                    id
                    slug
                    name
                }
                children {
                    id
                    slug
                    featuredAsset {
                        ...Asset
                    }
                    name
                }
                customFields {
                    itemOrderData
                }
            }
        }
    }
    ${WEB_PRESENCE_FRAGMENT}
    ${PAGE_COMPONENT_FRAGMENT}
    ${ASSET_FRAGMENT}
`;

export const GET_POLICY_PAGE = gql`
    query GetPolicyPage($identifier: String!, $slug: String!) {
        policyPage(identifier: $identifier, slug: $slug) {
            path
            type
            title
            content
        }
    }
`;

export const APPLY_COUPON = gql`
    mutation ApplyCoupon($couponCode: String!) {
        applyCouponCode(couponCode: $couponCode) {
            ... on Order {
                id
                code
                subTotal
                discounts {
                    description
                    amount
                    amountWithTax
                }
            }
            ... on CouponCodeExpiredError {
                message
            }
            ... on CouponCodeInvalidError {
                message
            }
            ... on CouponCodeLimitError {
                message
            }
        }
    }
`;

export const REMOVE_COUPON = gql`
    mutation RemoveCoupon($couponCode: String!) {
        removeCouponCode(couponCode: $couponCode) {
            ... on Order {
                id
                code
                subTotal
                discounts {
                    description
                    amount
                    amountWithTax
                }
            }
        }
    }
`;

export const ADD_TO_CART = gql`
    mutation AddToCart($variantId: ID!, $qty: Int!) {
        addItemToOrder(productVariantId: $variantId, quantity: $qty) {
            ...Cart
            ...ErrorResult
            ...on InsufficientStockError {
                order {
                    ...Cart
                }
            }
        }
    }
    ${CART_FRAGMENT}
    ${ERROR_RESULT_FRAGMENT}
`;

export const ADD_TO_CART_WITH_CUSTOMIZIBLE_ASSETS = gql`
    mutation AddToCartWithCustomizableAssets($variantId: ID!, $qty: Int!, $customizableAssets: [CustomizableAssetInput!]) {
        addItemToOrderWithCustomizableAssets(productVariantId: $variantId, quantity: $qty, customizableAssets: $customizableAssets) {
            ...Cart
            ...ErrorResult
            ...on OrderInterceptorError {
                errorCode
                message
                interceptorError
            }
            ...on InsufficientStockError {
                order {
                    ...Cart
                }
            }
        }
    }
    ${CART_FRAGMENT}
    ${ERROR_RESULT_FRAGMENT}
`;

export const ADJUST_ITEM_QUANTITY = gql`
    mutation AdjustItemQuantity($id: ID!, $qty: Int!) {
        adjustOrderLine(orderLineId: $id, quantity: $qty){
            ...Cart
            ...ErrorResult
        }
    }
    ${CART_FRAGMENT}
    ${ERROR_RESULT_FRAGMENT}
`;

export const REMOVE_ITEM_FROM_CART = gql`
    mutation RemoveItemFromCart($id: ID!) {
        removeOrderLine(orderLineId: $id){
            ...Cart
            ...ErrorResult
        }
    }
    ${CART_FRAGMENT}
    ${ERROR_RESULT_FRAGMENT}
`;

export const GET_COUPON_WITH_PRODUCTS = gql`
  query GetCouponWithProducts($code: String!) {
    promotionByPromoLinkCode(promoLinkCode: $code) {
      promotion {
        id
        couponCode
        name
        description
        endsAt
        customFields {
            label
            promotionLink
        }
      }
      allProducts
      variants {
        id
        name
        price
        currencyCode
        priceWithTax
        sku
        stockLevel
        featuredAsset {
            id
            preview
            focalPoint {
              x
              y
            }
            imageUrl
        }
        customFields {
            MSRP
            alternativeFeaturedAssets {
                id
                preview
                focalPoint {
                    x
                    y
                }
                imageUrl
            }
        }
        customizationOptions {
            ...ProductCustomizationOption
        }
        product {
            id
            name
            slug
            description
            customizationOptions {
                ...ProductCustomizationOption
            }
            customFields {
                defaultVariant {
                    id
                }
                alternativeFeaturedAssets {
                    id
                    preview
                    focalPoint {
                        x
                        y
                    }
                    imageUrl
                }
            }
            featuredAsset {
                id
                preview
                focalPoint {
                    x
                    y
                }
                imageUrl
            }
        }
      }
      products {
        id
        name
        slug
        description
        featuredAsset {
            id
            preview
            focalPoint {
                x
                y
            }
            imageUrl
        }
        customFields {
            defaultVariant {
                id
            }
            alternativeFeaturedAssets {
                id
                preview
                focalPoint {
                    x
                    y
                }
                imageUrl
            }
        }
        customizationOptions {
            ...ProductCustomizationOption
        }
        variants {
            id
            name
            sku
            price
            currencyCode
            priceWithTax
            stockLevel
            customFields {
                MSRP
            }
        }
      }
    }
  }
  ${PRODUCT_CUSTOMIZATION_OPTION_FRAGMENT}
`;

export const VALIDATE_PREVIEW_ACCESS = gql`
  query ValidatePreviewAccess($identifier: String!, $token: String!) {
    validatePreviewAccess(identifier: $identifier, token: $token)
  }
`;

export const GET_PRINT_FONT_CATALOG = gql`
  query GetPrintFontCatalog {
    printFontCatalog {
      family
      category
      googleFontsUrl
    }
  }
`;
